/**
 * Tipos da área Comercial — deals (negociações), atividades, fases do funil.
 *
 * Modelado a partir do funil real da IGM Security (Agendor): Lead → Qualificação →
 * Proposta → Negociação → Aprovação → Ganho/Perdido. Os tipos servem de contrato
 * com o backend futuro (Frente A + integração Agendor/Omie).
 *
 * IDs são strings (UUIDs no backend), datas são ISO strings.
 */

/**
 * Slugs canônicos do funil comercial. Mantido como union pra autocomplete em
 * referências hardcoded (filtros default, OPEN_STAGES, etc).
 *
 * Note que `Deal.fase` é `string` (não `DealStage`) — porque o admin pode criar
 * colunas custom em runtime via `useKanbanColumns.addColumn` (2026-05-18). Slugs
 * custom (snake_case do label) ficam armazenados em `Deal.fase` lado a lado com
 * os canônicos. `getStageInfo()` retorna stub default pra slugs desconhecidos
 * (não-canônicos), permitindo coexistência sem quebrar lookups.
 */
export type DealStage =
  | 'lead'
  | 'qualificacao'
  | 'proposta'
  | 'negociacao'
  | 'aprovacao'
  | 'ganho'
  | 'perdido';

/**
 * Prioridade do deal (dado persistido).
 *
 * Normalizada com `service_orders.prioridade` em 2026-05-20 (Bloco D.1 / Migration 033):
 *   - 'media' → 'normal' (mapping automático na migration)
 *   - 'critica' → 'urgente' (mapping automático)
 * Antes: TS expunha só 3 valores ('baixa'|'media'|'alta') enquanto o CHECK
 * SQL aceitava 4 ('baixa'|'media'|'alta'|'critica') — drift tech-debt resolvido
 * nesta rodada.
 *
 */
export type DealPriority = 'baixa' | 'normal' | 'alta' | 'urgente';

export type ActivityType =
  | 'criacao'
  | 'ligacao'
  | 'email'
  | 'reuniao'
  | 'proposta'
  | 'nota'
  | 'fase_alterada'
  | 'automation';

export interface DealActivity {
  id: string;
  dealId: string;
  type: ActivityType;
  title: string;
  description?: string;
  userId: string;
  userName: string;
  createdAtIso: string;
}

export interface Deal {
  id: string;
  /**
   * FEAT-V2-034 / BUG-V2-045: número sequencial humano (ex: 1000) preenchido
   * pelo backend. NULL pra deals antigos pré-migration 050 (fallback no UI
   * cai em `id` UUID).
   */
  numeroHumano?: number | null;
  /**
   * FEAT-V2-034 / BUG-V2-045: label pronto pra exibir (ex: "#1000").
   * Pré-resolvido pelo backend (shapeDeal) pra UI não precisar montar `#N`.
   * Quando `numero_humano` IS NULL, vem como `id` UUID (fallback defensivo).
   * UI **DEVE** preferir `humanLabel ?? id` em qualquer render textual.
   */
  humanLabel?: string;
  titulo: string;
  /**
   * FK pra `clients.id` (sempre `client-<uuid>` em deals criados via UI a
   * partir de 2026-05-18 — BUG-V2-001). Pra renderizar o nome legível, use
   * `useClientNome(clienteId)` de `clients-store.ts`.
   *
   * **Compat legado:** seeds antigos (`server/db/seed-deals.ts`) gravam
   * razão social direta como `Condomínio Saint Michael`. `useClientNome`
   * detecta o prefixo `client-` e faz lookup; valores legados são exibidos
   * como-is. R4 (CRM próprio completo) vai migrar seeds + renomear coluna
   * SQL pra `client_id` formalmente.
   */
  clienteId: string;
  /** Item físico — ex: "Câmera PTZ Hikvision", "Central Intelbras AMT 2018". Separado de `servico` desde 2026-05-16. */
  produto: string;
  /** Serviço/escopo — ex: "Instalação + configuração", "Manutenção preventiva trimestral". Separado de `produto` desde 2026-05-16. */
  servico: string;
  /** Valor proposto em reais (sem formatação) */
  valor: number;
  /**
   * Slug da coluna onde o card está. Aceita um `DealStage` canônico OU um slug
   * custom criado pelo admin via `useKanbanColumns.addColumn`. Use `getStageInfo`
   * pra resolver labels/variants — ela trata slugs desconhecidos como neutral.
   */
  fase: string;
  prioridade: DealPriority;
  /** ID do usuário responsável — referência ao admin-store users */
  responsavelId: string;
  /** Nome denormalizado pra evitar lookups em renderização de listas */
  responsavelNome: string;
  proximaAcao?: string;
  /** Data ISO da próxima ação agendada */
  proximaAcaoDataIso?: string;
  criadoEmIso: string;
  /** Atualizado por qualquer mutação no deal (fase, valor, atividade, etc) */
  atualizadoEmIso: string;
  observacoes?: string;
  /** Canal de origem do lead (Indicação, Site, Google Ads, etc) */
  origem?: string;
  /**
   * Projetos 2.0 Parte 2 / P1: tipo da venda. Só `'projeto'` gera projeto
   * automático ao entrar em "Ganho". `'recorrente'` = venda de recorrente.
   * `null`/undefined = não classificado (default, comportamento legado).
   * Só editável quando a flag `PROJETOS_V2_ENABLED` está ON.
   */
  tipoVenda?: 'projeto' | 'recorrente' | null;
  /**
   * Projetos 2.0 F1.1 / P1: FK pra `clientes` (V2) — distinta de `clienteId`
   * (ref V1/texto legado). É o cliente que a automação deal→projeto herda.
   * `null` quando não preenchido (backfill por nome não casou ou venda antiga).
   */
  clienteV2Id?: string | null;
  /**
   * BUG-B1.6.2 (2026-05-28): descricao do chamado quando origem='portal_cliente'
   * (workflow corretiva). Opcional pra preservar compat com deals Comercial
   * que não usam. Backend mapDeal extrai de fields_json.descricao.
   */
  descricao?: string;
  /**
   * BUG-B1.6.2 (2026-05-28): R2 key da foto anexada ao chamado (quando vier
   * do Portal Cliente). Frontend admin gera URL presigned ao clicar. Opcional
   * — null/undefined pra deals sem foto.
   */
  fotoR2Key?: string | null;
  /**
   * FEAT-V2-035 / BUG-V2-047: soft delete flag. true = ativo (default,
   * aparece em Kanban). false = inativo (oculto por default, badge cinza
   * quando filtro "Mostrar inativos" estiver ON). Backend pré-migration 051
   * envia `true` por compat.
   */
  ativo?: boolean;
  /**
   * BUG-V2-058 (2026-05-25): tags pré-carregadas pelo backend (batch query)
   * pra render no header do DealCard sem N+1. Regra 13 do padrão Kanban.
   * Shape mínimo compatível com `<TagBadge>` (TagsCatalog component).
   */
  tags?: Array<{
    id: string;
    nome: string;
    cor: string;
    icone: string | null;
    ativo: boolean;
  }>;
  atividades: DealActivity[];
}

/**
 * Catálogo de fases — ordem do funil + labels pt-BR + variant de Badge pra UI.
 * Único ponto de verdade — UI consome esse array em filtros, sort, badges, etc.
 */
export const DEAL_STAGES: ReadonlyArray<{
  slug: DealStage;
  label: string;
  ordem: number;
  variant: 'neutral' | 'info' | 'warning' | 'success' | 'danger';
  description: string;
}> = [
  { slug: 'lead', label: 'Lead', ordem: 1, variant: 'neutral', description: 'Contato inicial qualificado' },
  { slug: 'qualificacao', label: 'Qualificação', ordem: 2, variant: 'neutral', description: 'Entendendo necessidade do cliente' },
  { slug: 'proposta', label: 'Proposta', ordem: 3, variant: 'info', description: 'Orçamento enviado, aguardando retorno' },
  { slug: 'negociacao', label: 'Negociação', ordem: 4, variant: 'warning', description: 'Ajustando termos comerciais' },
  { slug: 'aprovacao', label: 'Aprovação', ordem: 5, variant: 'warning', description: 'Aprovado pelo cliente, fechando contrato' },
  { slug: 'ganho', label: 'Ganho', ordem: 6, variant: 'success', description: 'Negócio fechado' },
  { slug: 'perdido', label: 'Perdido', ordem: 7, variant: 'danger', description: 'Encerrado sem venda' },
] as const;

/**
 * Fases em aberto — exclui ganho/perdido. Usado como default do filtro "apenas
 * ativos" + getCardHealth. Tipado como `ReadonlyArray<string>` pra aceitar slugs
 * custom no `.includes()` sem cast (regra j do padrão Kanban — 2026-05-18).
 * Não significa que outros slugs sejam "open"; significa só que o type system
 * não reclama do lookup.
 */
export const OPEN_STAGES: ReadonlyArray<string> = [
  'lead',
  'qualificacao',
  'proposta',
  'negociacao',
  'aprovacao',
];

/**
 * Resolve info de uma fase do funil. Sempre retorna um shape válido — pra slugs
 * canônicos, o registro do DEAL_STAGES; pra slugs custom (criados pelo admin),
 * um stub neutral com label igual ao slug (caller real deve usar
 * `useKanbanColumns.getLabel(slug)` pra obter o label legível).
 */
export function getStageInfo(slug: string) {
  const found = DEAL_STAGES.find((s) => s.slug === slug);
  if (found) return found;
  return {
    slug: slug as DealStage,
    label: slug,
    ordem: 99,
    variant: 'neutral' as const,
    description: '',
  };
}

/** Calcula dias sem interação a partir da última atividade ou atualização do deal. */
export function getDiasParado(deal: Deal, now: Date = new Date()): number {
  const last = deal.atividades.length
    ? Math.max(...deal.atividades.map((a) => new Date(a.createdAtIso).getTime()))
    : new Date(deal.atualizadoEmIso).getTime();
  const diffMs = now.getTime() - last;
  return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
}

// Fase E3 da exclusão do comercial v1: `getUrgency`/`URGENCY_INFO`/`DealUrgency`
// saíram — eram exclusivos do kanban/lista/detalhe v1. A saúde genérica dos
// cards segue em `src/lib/card-health.ts`.

// Labels normalizadas com service_orders.prioridade em 2026-05-20 (Bloco D.1 / Migration 033).
// Antes: { baixa, media: 'Média', alta }. Mapping: media→normal, critica→urgente.
export const PRIORITY_LABELS: Record<DealPriority, string> = {
  baixa: 'Baixa',
  normal: 'Normal',
  alta: 'Alta',
  urgente: 'Urgente',
};

export const ACTIVITY_LABELS: Record<ActivityType, { label: string; icon: string }> = {
  criacao: { label: 'Card criado', icon: '✨' },
  ligacao: { label: 'Ligação', icon: '📞' },
  email: { label: 'Email', icon: '✉️' },
  reuniao: { label: 'Reunião', icon: '🤝' },
  proposta: { label: 'Proposta enviada', icon: '📄' },
  nota: { label: 'Nota', icon: '📝' },
  fase_alterada: { label: 'Fase alterada', icon: '➡️' },
  automation: { label: 'Automação', icon: '🤖' },
};

/** Filtros aplicados na ComercialPage — persistidos via useUiPreference (`comercial.filters` em user_ui_preferences). */
export interface ComercialFilters {
  search: string;
  /** Slugs (canônicos ou custom). Vazio = todas (mas se apenasAtivos=true, restringe a OPEN_STAGES). */
  fases: string[];
  responsavelId: string | null; // null = todos
  valorMin: number | null;
  valorMax: number | null;
  apenasAtivos: boolean;
}

export const DEFAULT_FILTERS: ComercialFilters = {
  search: '',
  fases: [],
  responsavelId: null,
  valorMin: null,
  valorMax: null,
  apenasAtivos: true,
};

export type SortField = 'cliente' | 'valor' | 'criadoEm' | 'atualizadoEm' | 'diasParado' | 'fase';
export type SortDir = 'asc' | 'desc';

export interface SortState {
  field: SortField;
  dir: SortDir;
}

export const DEFAULT_SORT: SortState = { field: 'diasParado', dir: 'desc' };
